import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { AlertContent } from "@redotech/redo-merchant-app-common/alert";
import { AuthRequired } from "@redotech/redo-merchant-app-common/auth";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import {
  TeamAndUserRequired,
  TeamContext,
} from "@redotech/redo-merchant-app-common/team";
import { UserCacheContext } from "@redotech/redo-merchant-app-common/user-cache";
import { VoiceWidget } from "@redotech/redo-merchant-support-app/voice/voice-widget";
import { GeneralPermissions } from "@redotech/redo-model/permissions";
import { ActionScopeProvider } from "@redotech/redo-web/actions/action-scope-context";
import { AiSlidePanelLayout } from "@redotech/redo-web/ai/chat/components/ai-slide-panel-layout";
import { ErrorBoundary } from "@redotech/redo-web/error-page";
import { Page as CommonPage } from "@redotech/redo-web/page";
import { TreeProvider } from "@redotech/redo-web/tree/tree-context";
import { memo, useEffect, useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import { useEffect_conversionNotifications } from "../conversion/common/hooks/use-conversion-notifications";
import { DataServices } from "../services/data-services";
import { NotificationBanner } from "./banner/banner";
import { InChatEscalationFlagProvider } from "./help-chat/in-chat-escalation-flag-provider";
import { NavigationVersionProvider } from "./navigation/navigation-version-context";
import { GlobalAI } from "./navigation/side-navigation/global-ai";
import { MerchantAppSideNav } from "./navigation/side-navigation/merchant-side-navigation";
import { GlobalSearch } from "./navigation/top-navigation/global-search";
import { MerchantAppTopNav } from "./navigation/top-navigation/merchant-top-navigation";

function urlIsInSettings(url: string): boolean {
  const matchesStoreUrl = matchPath("/stores/:storeId/settings/*", url);
  const matchesSettingsRoute = matchPath("/settings/*", url);
  return !!(matchesStoreUrl || matchesSettingsRoute);
}

// Do not show incident banners for warehouse routes. Warehouse workers do not care about incidents.
function isBannerRoute(url: string): boolean {
  const hiddenPaths = [
    "/stores/:storeId/order-management/outbound-labels/pick-sessions/*",
    "/stores/:storeId/order-management/outbound-labels/pick-sessions",
    "/stores/:storeId/order-management/outbound-labels/create-pick-session/*",
    "/stores/:storeId/order-management/outbound-labels/create-pick-session",
    "/stores/:storeId/order-management/wms/work-orders/detail/:workOrderId",
    "/stores/:storeId/order-management/warehouse",
    "/stores/:storeId/order-management/warehouse/*",
  ];
  return !hiddenPaths.some((path) => matchPath(path, url));
}

export const Page = memo(function Page({
  hideHeader = () => false,
  hideNavbar = () => false,
  hidePadding = (url: string) => url.includes("ai-builder"),
  hideSearchBarAndBanner = () => false,
}: {
  hideHeader?: (url: string, search: { search: string }) => boolean;
  hideNavbar?: (url: string) => boolean;
  hidePadding?: (url: string, search: { search: string }) => boolean;
  hideSearchBarAndBanner?: (url: string) => boolean;
}) {
  return (
    <AuthRequired>
      <TeamAndUserRequired>
        {/*
          NavigationVersionProvider must wrap DataServices so that
          ProductSuiteProvider → MerchantRoutesProvider (which lives
          inside DataServices) can read NavigationVersionContext when
          building flag-aware base URLs like `shippingBaseUrl` and
          `returnsBaseUrl`. If this provider sits below DataServices,
          MerchantRoutesProvider reads the default value
          (`newNavigationEnabled: false`) and every new-nav href
          silently collapses to the legacy `/order-management/*` tree.
          NavigationVersionProvider itself reads `TeamContext`, which
          is provided by `TeamAndUserRequired` above.
        */}
        <NavigationVersionProvider>
          <DataServices>
            <UserCacheContext.Provider value={new Map()}>
              <PageWithContext
                hideHeader={hideHeader}
                hideNavbar={hideNavbar}
                hidePadding={hidePadding}
                hideSearchBarAndBanner={hideSearchBarAndBanner}
              />
            </UserCacheContext.Provider>
          </DataServices>
        </NavigationVersionProvider>
      </TeamAndUserRequired>
    </AuthRequired>
  );
});

const PageWithContext = memo(function PageWithContext({
  hideHeader = () => false,
  hideNavbar = () => false,
  hidePadding = (url: string) => url.includes("ai-builder"),
  hideSearchBarAndBanner = () => false,
}: {
  hideHeader?: (url: string, search: { search: string }) => boolean;
  hideNavbar?: (url: string) => boolean;
  hidePadding?: (url: string, search: { search: string }) => boolean;
  hideSearchBarAndBanner?: (url: string) => boolean;
}) {
  const team = useRequiredContext(TeamContext);

  const [lastNonSettingsUrl, setLastNonSettingsUrl] = useState<
    string | undefined
  >(undefined);

  const location = useLocation();

  const stableHideNavbar = useHandler(hideNavbar);
  const stableHidePadding = useHandler(hidePadding);
  const stableHideSearchBarAndBanner = useHandler(hideSearchBarAndBanner);

  const shouldHidePadding = useMemo(() => {
    return stableHidePadding(location.pathname, { search: location.search });
  }, [location, stableHidePadding]);

  const hideNavigation = useMemo(() => {
    return stableHideNavbar(location.pathname);
  }, [location, stableHideNavbar]);

  const shouldHideSearchBarAndBanner = useMemo(() => {
    return stableHideSearchBarAndBanner(location.pathname);
  }, [location, stableHideSearchBarAndBanner]);

  useEffect(() => {
    if (!urlIsInSettings(location.pathname)) {
      setLastNonSettingsUrl(location.pathname);
    }
  }, [location]);

  const routes = useRequiredContext(MerchantRoutesContext);
  const canManageUsers = useHasPermission(GeneralPermissions.MANAGE_TEAM_USERS);

  const settingsUrl = canManageUsers
    ? routes.getSettingsSubRoute("users")
    : routes.getSettingsSubRoute("personal/profile");

  const nonSettingsUrl = lastNonSettingsUrl || `/stores/${team._id}/summary`;
  const settingsRoot = `/stores/${team._id}/settings`;

  const intercom = useIntercom();

  useEffect_conversionNotifications();

  // Hacky: hide global search top bar on WMS routes
  const isWmsRoute =
    location.pathname.includes("/pick-sessions") ||
    location.pathname.includes("/create-pick-session") ||
    location.pathname.includes("/personalized-slips") ||
    location.pathname.includes("/returns/processing") ||
    location.pathname.includes("/returns/sorting") ||
    location.pathname.includes("/warehouse") ||
    /\/wms\/work-orders\/detail\/\d+/.test(location.pathname);

  const hideGlobalSearchTopBar = isWmsRoute || shouldHideSearchBarAndBanner;
  const showBanner =
    isBannerRoute(location.pathname) && !shouldHideSearchBarAndBanner;

  return (
    <TreeProvider>
      <ActionScopeProvider>
        <GlobalSearch>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100vh",
            }}
          >
            {!hideGlobalSearchTopBar && <MerchantAppTopNav />}
            <AiSlidePanelLayout
              panel={
                <InChatEscalationFlagProvider>
                  <GlobalAI />
                </InChatEscalationFlagProvider>
              }
              panelWidth="sm"
            >
              <CommonPage
                banner={showBanner ? <NotificationBanner /> : undefined}
                ErrorBoundary={ErrorBoundary}
                hideHeader={hideHeader}
                hidePadding={shouldHidePadding}
                nav={
                  <MerchantAppSideNav
                    enterSettingsUrl={settingsUrl}
                    exitSettingsUrl={nonSettingsUrl}
                    hideNav={hideNavigation}
                    hideTeamSwitcher
                    settingsRoot={settingsRoot}
                  />
                }
                showNewIntercomMessage={() => intercom.showNewMessage()}
                skipFirstBreadcrumbIfMatches={["Order management"]}
              />
              <AlertContent />
              <VoiceWidget />
            </AiSlidePanelLayout>
          </div>
        </GlobalSearch>
      </ActionScopeProvider>
    </TreeProvider>
  );
});
